import { fromEvent, Observable, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LogLevel } from '../../diagnostics/log-level';
import { CachedDataStore } from './cached-data-store';

/**
 * Defines a single value data store using browser storage as the underlying storage mechanism
 * This is used as a base class for LocalDataStore and SessionDataStore which use LocalStorage and SessionStorage respectively
 */
export abstract class BrowserStorageDataStore<TData> extends CachedDataStore<TData, string> {

    /**
     * Initializes a new instance of DataStore<T>
     */
    constructor(protected storageKey: string, protected storage: Storage) {
        super();
        this.listenForStorageChanges();
    }

    /**
     * Implementation to get the stored data
     */
    protected getData(): Observable<string> {
        return this.storage ? of(this.storage.getItem(this.storageKey)) : of('');
    }

    /**
     * Implementation to set the stored data
     */
    protected setData(data: string): Observable<void> {
        this.storage?.setItem(this.storageKey, data);
        return of(null);
    }

    /**
     * Implementation to clear the stored data
     */
    protected clearData(): Observable<void> {
        this.storage?.removeItem(this.storageKey);
        return of(null);
    }

    /**
     * Transforms data in preparation for storage. Default behavior is no operation
     */
    protected transformToStore(data: TData): string {
        return JSON.stringify(data);
    }

    /**
     * Transforms data in preparation for usage. Default behavior is no operation
     */
    protected transformFromStore(storedData: string): TData {
        let data: TData = null;
        try {
            data = JSON.parse(storedData);
        } catch (error) {
            this.log(`Failed to parse data retrieved with storage key: ${this.storageKey}`, LogLevel.Debug);
        }
        return data;
    }

    /**
     * Listens for when other windows have modified our storage key and emits when the stored data has changed.
     */
    private listenForStorageChanges(): void {
        this.rxjsLifetime.addSubscriptions(
            fromEvent(window, 'storage').pipe(
                filter((event: StorageEvent) => {
                    return event.storageArea === this.storage && event.key === this.storageKey;
                })
            ).subscribe((event: StorageEvent) => {
                const data = this.transformFromStore(event.newValue);
                this.dataChangedSubject.next(data);
            })
        );
    }
}
